import type { IndexRoute as FireflyIndexRoute, NonIndexRoute as FireflyNonIndexRoute } from "@squide/firefly";

interface RouteHandle {
    trackingName?: string;
    pageTitle?: string;

    /** Mixpanel `Location` property for the page. Inherited by nested routes through the match chain. */
    mixpanelLocation?: string;

    /**
     * Mixpanel `Sublocation` property for the page. Only applies at or below the route that sets `mixpanelLocation`.
     * The function form receives the match's params and the current search params; an empty result sets nothing.
     */
    mixpanelSublocation?:
        | string
        | ((args: {
              params: Record<string, string | undefined>;
              searchParams: URLSearchParams;
          }) => string | null | undefined);

    /* Marks a route as part of/involved in the onboarding flow, so onboarding gates render it instead
     * of enforcing their prerequisite. Unlike `isZeroState`, this isn't onboarding-exclusive — it also
     * serves already-onboarded workspaces.
     */
    isOnboardingStep?: boolean;

    /**
     * Marks the setup flow, where the region the user picked lives only in the browser until consent saves it,
     * so the shell's startup check must not clear it for a workspace that has no saved region yet.
     */
    isSetupFlow?: boolean;

    /**
     * Marks Migrate's legacy assessment pages, where the user picks the region in the browser, so the shell's
     * startup check must not replace it with the workspace's region.
     */
    isMigrateAssessmentFlow?: boolean;

    /**
     * Path (with `:workspaceId`) of the zero-state route a section redirects to when the workspace
     * is not consented and the Unified Experience flag is on. Inherited by nested routes through the
     * match chain, so it only needs to be set on each top-level section route.
     */
    zeroStatePath?: string;
    /** Marks a route as a zero-state route so the consent guard renders it instead of redirecting away. */
    isZeroState?: boolean;

    /**
     * Path (with `:workspaceId`) of the restricted-state route to redirect to when the session's
     * product access for this section is off. Inherited by nested routes through the match chain,
     * so it only needs to be set on each top-level section route.
     */
    restrictedStatePath?: string;
    /** Marks a route as a restricted-state route so the access guard renders it instead of redirecting away. */
    isRestrictedState?: boolean;
}

interface NonIndexRoute extends Omit<FireflyNonIndexRoute, "children"> {
    children?: Route[];
    handle?: RouteHandle;
}

interface IndexRoute extends FireflyIndexRoute {
    handle?: RouteHandle;
}

type Route = IndexRoute | NonIndexRoute;

function isRouteHandle(handle: unknown): handle is RouteHandle {
    return typeof handle === "object" && handle !== null;
}

export { type IndexRoute, type NonIndexRoute, type Route, type RouteHandle, isRouteHandle };
